import { useAccount, useMsal } from "@azure/msal-react";
import { type UseQueryResult, useQuery } from "@tanstack/react-query";
import type { PromptTemplate } from "src/hooks/useConfiguration";
import { deleteAPI, fetchWithAuth, getAPI } from "src/utils/fetch";
import { isWidgetMode } from "src/utils/isWidgetMode";

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;
const PROMPT_TEMPLATES_URL = `${BACKEND_URL}/v1/system/prompt-templates`;

export async function fetchPromptTemplates(instance): Promise<PromptTemplate[]> {
    const response = await getAPI({
        url: PROMPT_TEMPLATES_URL,
        instance,
    });
    return await response.json();
}

/**
 * Create or update a prompt template. Returns a callback usable by both the admin manage
 * view and the user-facing "create template" flow in the picker modal.
 */
export const useUpsertPromptTemplate = () => {
    const { instance } = useMsal();

    return async (data: Partial<PromptTemplate>) => {
        const response = await fetchWithAuth({
            url: PROMPT_TEMPLATES_URL,
            method: "PUT",
            instance,
            payload: data,
        });

        if (!response.ok) {
            throw new Error("Failed to save prompt template");
        }

        return response.json();
    };
};

export const useDeletePromptTemplate = () => {
    const { instance } = useMsal();

    return async (id: string) => {
        const response = await deleteAPI({
            url: `${PROMPT_TEMPLATES_URL}?id=${encodeURIComponent(id)}`,
            instance,
        });

        if (!response.ok) {
            throw new Error("Failed to delete prompt template");
        }

        return response.json();
    };
};

export const usePromptTemplates = (): UseQueryResult<PromptTemplate[], Error> => {
    const { instance, inProgress } = useMsal();
    const account = useAccount();
    const isAuthenticated = !!account?.idToken;

    return useQuery<PromptTemplate[], Error>({
        queryKey: ["prompt-templates"],
        queryFn: () => fetchPromptTemplates(instance),
        enabled: isAuthenticated && inProgress === "none" && !isWidgetMode(),
        retry: false,
        staleTime: Number.POSITIVE_INFINITY,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
    });
};
